import { useEffect, useState } from "react";
import { LogOut, Lock, Search } from "lucide-react";
import { toast, Toaster } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { adminLogin } from "../lib/api";
import ProductionTab from "./admin/ProductionTab";
import SharedCartsTab from "./admin/SharedCartsTab";
import MarketingParentTab from "./admin/MarketingParentTab";
import DashboardTab from "./admin/DashboardTab";
import OrdersTab from "./admin/OrdersTab";
import TrafficTab from "./admin/TrafficTab";
import AuditLogTab from "./admin/AuditLogTab";
import ProductsParentTab from "./admin/ProductsParentTab";
import DiscountsTab from "./admin/DiscountsTab";
import QuotesTab from "./admin/QuotesTab";
import EmailsParentTab from "./admin/EmailsParentTab";
import ReviewsTab from "./admin/ReviewsTab";
import DesignsTab from "./admin/DesignsTab";
import InvoicesParentTab from "./admin/InvoicesParentTab";
import HealthTab from "./admin/HealthTab";
import EtsyTab from "./admin/EtsyTab";
import LeadsTab from "./admin/LeadsTab";
// JobsTab removed — paid invoices flow into the Orders tab now.
import NotificationBell from "../components/AdminNotificationBell";
import AdminCommandPalette from "../components/AdminCommandPalette";

const KEY = "tc_admin_pw";
// iter76c — big consolidation. Top-level tabs dropped from 24 → 15 by
// bundling: (a) Custom Products + Ready-Made under `Products`; (b) all
// marketing/integration settings under `Marketing & Ops`; (c) invoice
// list + settings under `Invoices`; (d) Email Calendar + Health under
// `Emails`. Webhook Test + Alt-Text audit cards moved into Health tab.
const TABS = [
  { k: "dashboard", l: "Dashboard" },
  { k: "orders", l: "Orders" },
  { k: "production", l: "Production" },
  { k: "products", l: "Products" },
  { k: "discounts", l: "Discounts" },
  { k: "reviews", l: "Reviews" },
  { k: "designs", l: "Designs" },
  { k: "invoices", l: "Invoices" },
  { k: "quotes", l: "Quote Requests" },
  { k: "shared-carts", l: "Shared Carts" },
  { k: "marketing", l: "Marketing & Ops" },
  { k: "emails", l: "Emails" },
  { k: "integrations", l: "Integrations" },
  { k: "leads", l: "Lead Finder" },
  { k: "health", l: "Health · Verification" },
  { k: "audit", l: "Audit Log" },
  { k: "traffic", l: "Traffic" },
];

export default function Admin() {
  const [pw, setPw] = useState(localStorage.getItem(KEY) || "");
  const [loggedIn, setLoggedIn] = useState(false);
  const validTabs = TABS.map((t) => t.k);
  const readHashTab = () => {
    const h = (window.location.hash || "").replace(/^#/, "");
    return validTabs.includes(h) ? h : "dashboard";
  };
  const [tab, setTabState] = useState(readHashTab);
  const setTab = (t) => {
    setTabState(t);
    if (t && `#${t}` !== window.location.hash) window.location.hash = t;
  };
  // Keep tab in sync when the URL hash changes (back/forward, reload).
  useEffect(() => {
    const onHash = () => setTabState(readHashTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pw) {
      adminLogin(pw).then(() => { localStorage.setItem(KEY, pw); setLoggedIn(true); })
        .catch(() => { localStorage.removeItem(KEY); setLoggedIn(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault();
    try { await adminLogin(pw); localStorage.setItem(KEY, pw); setLoggedIn(true); }
    catch { toast.error("Invalid password"); }
  };

  const logout = () => { localStorage.removeItem(KEY); setLoggedIn(false); setPw(""); };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-black">
        <Toaster theme="dark" position="top-right" closeButton toastOptions={{ classNames: { closeButton: "!absolute !top-2 !right-2 !left-auto !bg-transparent !border-0 !text-zinc-500 hover:!text-white !shadow-none !w-6 !h-6 !rounded-none" } }} />
        <Header />
        <section className="max-w-md mx-auto px-6 py-32" data-testid="admin-login">
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ ADMIN</div>
          <h1 className="font-display text-5xl uppercase mb-8">Staff Login</h1>
          <form onSubmit={handleLogin} className="surface-card p-6 space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest text-zinc-500">PASSWORD</span>
              <input
                data-testid="admin-password" type="password"
                value={pw} onChange={(e) => setPw(e.target.value)}
                className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-3 mt-1 font-body text-sm outline-none"
              />
            </label>
            <button data-testid="admin-login-btn" type="submit" className="w-full btn-tc-primary flex items-center justify-center gap-2">
              <Lock size={16} /> Login
            </button>
          </form>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Toaster theme="dark" position="top-right" closeButton toastOptions={{ classNames: { closeButton: "!absolute !top-2 !right-2 !left-auto !bg-transparent !border-0 !text-zinc-500 hover:!text-white !shadow-none !w-6 !h-6 !rounded-none" } }} />
      <Header />
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ ADMIN_DASHBOARD</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase">Toronto Custom CMS</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-cmdk-hint"
              onClick={() => {
                // Fire the same keyboard event the palette listens for
                window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true, bubbles: true }));
              }}
              title="Global search — jump to any tab, product, order, or invoice (Cmd+K)"
              className="font-mono text-xs tracking-widest text-zinc-400 hover:text-white flex items-center gap-2 border border-[#222] px-4 py-2 hover:border-[var(--tc-red)]"
            >
              <Search size={14} /> SEARCH
              <kbd className="border border-[#333] px-1 py-0.5 text-[9px] text-zinc-500">⌘K</kbd>
            </button>
            <NotificationBell pw={pw} onJump={(t) => setTab(t)} />
            <button data-testid="admin-logout" onClick={logout} className="font-mono text-xs tracking-widest text-zinc-400 hover:text-white flex items-center gap-2 border border-[#222] px-4 py-2">
              <LogOut size={14} /> LOGOUT
            </button>
          </div>
        </div>

        {/* iter76d — Cmd+K palette, mounted once at the admin root */}
        <AdminCommandPalette pw={pw} onJump={setTab} />

        <div className="flex flex-wrap gap-2 mb-10 border-b border-[#222] pb-3 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`tab-btn ${tab === t.k ? "active" : ""}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardTab pw={pw} />}
        {tab === "orders" && <OrdersTab pw={pw} onJump={setTab} />}
        {tab === "production" && <ProductionTab pw={pw} />}
        {tab === "shared-carts" && <SharedCartsTab pw={pw} />}
        {tab === "marketing" && <MarketingParentTab pw={pw} />}
        {tab === "health" && <HealthTab pw={pw} />}
        {tab === "emails" && <EmailsParentTab pw={pw} />}
        {tab === "products" && <ProductsParentTab pw={pw} />}
        {tab === "discounts" && <DiscountsTab pw={pw} />}
        {tab === "reviews" && <ReviewsTab pw={pw} />}
        {tab === "designs" && <DesignsTab pw={pw} />}
        {tab === "invoices" && <InvoicesParentTab pw={pw} />}
        {tab === "quotes" && <QuotesTab pw={pw} />}
        {tab === "integrations" && <EtsyTab pw={pw} />}
        {tab === "leads" && <LeadsTab pw={pw} />}
        {tab === "audit" && <AuditLogTab pw={pw} />}
        {tab === "traffic" && <TrafficTab pw={pw} />}
      </section>
      <Footer />
    </div>
  );
}
